import React, {useState, useRef, useEffect} from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Switch,
  FormControlLabel,
  Alert,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Send as SendIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import emailSettingsApi from '@crema/services/apis/emailSettings';
import AddRecipientDialog from './AddRecipientDialog';
import DeleteRecipientDialog from './DeleteRecipientDialog';

const RecipientsList = ({recipients, onSuccess, onError}) => {
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedRecipient, setSelectedRecipient] = useState(null);
  const [sendingTest, setSendingTest] = useState(false);
  const isMountedRef = useRef(true);

  // Cleanup effect to prevent memory leaks
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const handleAddRecipient = () => {
    setSelectedRecipient(null);
    setAddDialogOpen(true);
  };

  const handleEditRecipient = (recipient) => {
    setSelectedRecipient(recipient);
    setEditDialogOpen(true);
  };

  const handleDeleteRecipient = (recipient) => {
    setSelectedRecipient(recipient);
    setDeleteDialogOpen(true);
  };

  const handleToggleStatus = async (recipient) => {
    try {
      const response = await emailSettingsApi.updateRecipient(recipient.id, {
        is_active: !recipient.is_active,
      });

      if (isMountedRef.current) {
        if (response.data.status === 200) {
          onSuccess(
            `Recipient ${
              recipient.is_active ? 'deactivated' : 'activated'
            } successfully`,
          );
        } else {
          onError(response.data.message || 'Failed to update recipient status');
        }
      }
    } catch (error) {
      console.error('Error toggling recipient status:', error);
      if (isMountedRef.current) {
        onError('Failed to update recipient status');
      }
    }
  };

  const handleSendTestEmail = async (recipient) => {
    try {
      if (isMountedRef.current) {
        setSendingTest(true);
      }
      const response = await emailSettingsApi.sendTestEmail({
        recipient_email: recipient.email,
        subject: 'Test Email - Daily Reports System',
        message: `Hello ${recipient.name},\n\nThis is a test email to verify your email configuration is working correctly.\n\nBest regards,\nDaily Reports System`,
      });

      if (isMountedRef.current) {
        if (response.data.status === 200 && response.data.data.emailSent) {
          onSuccess(`Test email sent successfully to ${recipient.email}`);
        } else {
          onError('Failed to send test email');
        }
      }
    } catch (error) {
      console.error('Error sending test email:', error);
      if (isMountedRef.current) {
        onError('Failed to send test email');
      }
    } finally {
      if (isMountedRef.current) {
        setSendingTest(false);
      }
    }
  };

  const activeRecipients = recipients.filter((r) => r.is_active);
  const inactiveRecipients = recipients.filter((r) => !r.is_active);

  return (
    <Box>
      <Box
        display='flex'
        justifyContent='space-between'
        alignItems='center'
        mb={3}
      >
        <Box>
          <Typography variant='h6' gutterBottom>
            Email Recipients
          </Typography>
          <Typography variant='body2' color='text.secondary'>
            Manage who receives daily reports
          </Typography>
        </Box>

        <Button
          variant='contained'
          startIcon={<AddIcon />}
          onClick={handleAddRecipient}
        >
          Add Recipient
        </Button>
      </Box>

      {recipients.length === 0 ? (
        <Alert severity='info'>
          No recipients configured. Add recipients to start receiving daily
          reports.
        </Alert>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align='center'>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {recipients.map((recipient) => (
                <TableRow key={recipient.id}>
                  <TableCell>
                    <Typography variant='body2' fontWeight='medium'>
                      {recipient.name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant='body2'>{recipient.email}</Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={recipient.is_active ? 'Active' : 'Inactive'}
                      color={recipient.is_active ? 'success' : 'default'}
                      size='small'
                      icon={
                        recipient.is_active ? (
                          <CheckCircleIcon />
                        ) : (
                          <CancelIcon />
                        )
                      }
                    />
                  </TableCell>
                  <TableCell align='center'>
                    <Box display='flex' gap={1} justifyContent='center'>
                      <IconButton
                        size='small'
                        onClick={() => handleEditRecipient(recipient)}
                        title='Edit recipient'
                      >
                        <EditIcon />
                      </IconButton>

                      <IconButton
                        size='small'
                        onClick={() => handleSendTestEmail(recipient)}
                        disabled={sendingTest}
                        title='Send test email'
                      >
                        <SendIcon />
                      </IconButton>

                      <FormControlLabel
                        control={
                          <Switch
                            checked={recipient.is_active}
                            onChange={() => handleToggleStatus(recipient)}
                            size='small'
                          />
                        }
                        label=''
                        sx={{m: 0}}
                      />

                      <IconButton
                        size='small'
                        onClick={() => handleDeleteRecipient(recipient)}
                        title='Delete recipient'
                        color='error'
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {recipients.length > 0 && (
        <Box mt={2}>
          <Typography variant='body2' color='text.secondary'>
            Active recipients: {activeRecipients.length} | Inactive recipients:{' '}
            {inactiveRecipients.length} | Total: {recipients.length}
          </Typography>
        </Box>
      )}

      {/* Add Recipient Dialog */}
      <AddRecipientDialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        onSuccess={(message) => {
          setAddDialogOpen(false);
          onSuccess(message);
        }}
        onError={onError}
      />

      {/* Edit Recipient Dialog */}
      <AddRecipientDialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        onSuccess={(message) => {
          setEditDialogOpen(false);
          onSuccess(message);
        }}
        onError={onError}
        recipient={selectedRecipient}
        isEdit={true}
      />

      {/* Delete Recipient Dialog */}
      <DeleteRecipientDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onSuccess={(message) => {
          setDeleteDialogOpen(false);
          onSuccess(message);
        }}
        onError={onError}
        recipient={selectedRecipient}
      />
    </Box>
  );
};

RecipientsList.propTypes = {
  recipients: PropTypes.array.isRequired,
  onSuccess: PropTypes.func.isRequired,
  onError: PropTypes.func.isRequired,
};

export default RecipientsList;
